import { SkyMessageBar, SkyText } from "@skypointcloud/skypoint-ui";
import { Link } from "wouter";
import { McpGrantListSection } from "@/components/mcp/McpGrantList";
import { useMcpWriter, useMyMcpGrants } from "@/hooks/use-mcp";
import { usePermissions } from "@/hooks/use-permissions";
import { ROUTES } from "@/lib/routes";

/**
 * Connected apps (ADR 0009 section 4): the AI apps the signed-in person has allowed to read (and,
 * since release 8B, change) skyData as them, with each one's scopes, across every instance, and
 * Disconnect. The change wording shows only where `useMcpWriter` holds (#1393 finding 61), the
 * same rule as the subtitle. Reached from the user menu; it needs
 * no instance (`personal` in `APP_ROUTES`). An ended connection stays listed with why, until the
 * cleanup removes it 30 days later.
 */
export default function ConnectedAppsPage() {
  const grants = useMyMcpGrants();
  const writer = useMcpWriter();
  const { hasPlatformScope } = usePermissions();
  // B-48: the consent refuses a platform administrator, so the page does not invite a connection;
  // it says where MCP is administered. The user menu does not link here for them either.
  if (hasPlatformScope) {
    return (
      <div className="flex flex-col gap-4" data-testid="connected-apps-page">
        <SkyMessageBar
          intent="info"
          layout="multiline"
          testId="connected-apps-platform-admin"
          title="Platform administrators cannot connect an AI app"
          message="An AI app acts as the person who connects it, and a platform administrator is not allowed to. To see every connection and to turn AI apps on or off, use Admin > AI apps."
        />
        <Link
          href={ROUTES.adminAiApps}
          className="text-sm font-medium text-accent-text hover:underline"
        >
          Open Admin &gt; AI apps
        </Link>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4" data-testid="connected-apps-page">
      <SkyText as="p" size={300} block>
        Each connection reads one instance as you, and only what you can see there yourself: never
        rows of your data and never credentials.{" "}
        {writer === false
          ? null
          : "A connection you allowed to make changes (skydata.write) acts as you too, and asks for your approval in skyData before larger changes. "}
        Connect an app from the app itself, by adding skyData as a connector.
      </SkyText>
      <McpGrantListSection
        query={grants}
        scope={{ kind: "mine" }}
        showInstance
        showUser={false}
        canRevoke
        ariaLabel="Your connected apps"
        emptyMessage="You have not connected an AI app yet."
        testId="connected-apps-grid"
      />
    </div>
  );
}
